import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Building, Warehouse, ArrowLeft, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const SizeSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDate, hourlyRate } = location.state || {};
  
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  const propertyTypes = {
    house: {
      icon: Home,
      title: 'House',
      sizes: [
        { range: '500-800', sqft: '500-800 sqft', label: 'Small House' },
        { range: '900-1200', sqft: '900-1200 sqft', label: 'Medium House' },
        { range: '1200-1600', sqft: '1200-1600 sqft', label: 'Large House' },
        { range: '1700-2000', sqft: '1700-2000 sqft', label: 'Very Large House' },
        { range: '2100-2500', sqft: '2100-2500 sqft', label: 'Extra Large House' },
        { range: '2600-3000', sqft: '2600-3000 sqft', label: 'Mansion' },
        { range: '3000-4000', sqft: '3000-4000 sqft', label: 'Large Mansion' },
        { range: '4000-5000', sqft: '4000-5000 sqft', label: 'Estate Home' }
      ]
    },
    apartment: {
      icon: Building,
      title: 'Apartment',
      sizes: [
        { range: '400-600', sqft: '400-600 sqft', label: 'Studio/1BR' },
        { range: '600-800', sqft: '600-800 sqft', label: '1-2 Bedroom' },
        { range: '800-1200', sqft: '800-1200 sqft', label: '2-3 Bedroom' },
        { range: '1200-1600', sqft: '1200-1600 sqft', label: '3-4 Bedroom' },
        { range: '1600-2000', sqft: '1600-2000 sqft', label: 'Large Apartment' },
        { range: '2000-2500', sqft: '2000-2500 sqft', label: 'Penthouse' }
      ]
    },
    warehouse: {
      icon: Warehouse,
      title: 'Warehouse/Commercial',
      sizes: [
        { range: '1000-2000', sqft: '1000-2000 sqft', label: 'Small Warehouse' },
        { range: '2000-5000', sqft: '2000-5000 sqft', label: 'Medium Warehouse' },
        { range: '5000-10000', sqft: '5000-10000 sqft', label: 'Large Warehouse' },
        { range: '10000+', sqft: '10000+ sqft', label: 'Industrial Space' }
      ]
    }
  };

  // Auto-navigate when both selections are made
  useEffect(() => {
    if (selectedPropertyType && selectedSize) {
      const timer = setTimeout(() => {
        navigate('/floor-selection', {
          state: {
            selectedDate,
            hourlyRate,
            propertyType: selectedPropertyType,
            propertySize: selectedSize
          }
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [selectedPropertyType, selectedSize, navigate, selectedDate, hourlyRate]);

  const handleSizeSelection = (sizeRange) => {
    setSelectedSize(sizeRange);
  };

  const handleGoBack = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 hover:shadow-xl hover:-translate-y-1 text-white border-gray-600 transition-all duration-300 transform"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Calendar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Booking Info */}
        {selectedDate && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-8 shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-700">Selected Date:</span>
                <span className="ml-2 font-medium text-teal-600">{selectedDate}</span>
              </div>
              <div>
                <span className="text-sm text-gray-700">Rate:</span>
                <span className="ml-2 font-medium text-red-600">${hourlyRate}/hour</span>
              </div>
            </div>
          </div>
        )}

        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Select Property Type & Size
          </h1>
          <p className="text-lg text-gray-700">
            Choose your property type and size to get accurate service recommendations
          </p>
        </div>

        {/* Property Type Selection - Only show if not selected */}
        {!selectedPropertyType && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Property Type</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(propertyTypes).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <Card
                    key={type}
                    className="cursor-pointer transition-all duration-200 hover:shadow-lg border-gray-200 hover:border-teal-600 hover:shadow-lg hover:-translate-y-1 hover:scale-105"
                    onClick={() => {
                      setSelectedPropertyType(type);
                      setSelectedSize(''); // Reset size when property type changes
                    }}
                  >
                    <CardHeader className="text-center">
                      <Icon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <CardTitle className="text-xl">{config.title}</CardTitle>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Size Selection */}
        {selectedPropertyType && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Property Size - {propertyTypes[selectedPropertyType].title}</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedPropertyType('');
                  setSelectedSize('');
                }}
                className="text-sm"
              >
                Change Property Type
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {propertyTypes[selectedPropertyType].sizes.map((size) => (
                <Card
                  key={size.range}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedSize === size.range
                      ? 'border-teal-600 bg-teal-50 shadow-md'
                      : 'border-gray-200 hover:border-teal-600 hover:shadow-lg hover:-translate-y-1 hover:scale-105'
                  }`}
                  onClick={() => handleSizeSelection(size.range)}
                >
                  <CardContent className="p-4 text-center">
                    <div className="font-medium text-teal-600 mb-1">{size.label}</div>
                    <div className="text-gray-700 font-semibold">{size.sqft}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {selectedPropertyType && selectedSize && (
          <div className="text-center">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-teal-600 mb-2">
                {propertyTypes[selectedPropertyType].title} - {selectedSize} sqft Selected
              </h3>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <div className="animate-spin h-4 w-4 border-2 border-teal-600 border-t-transparent rounded-full"></div>
                <span>Proceeding to floor selection...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SizeSelection;